import { Injectable, effect, inject } from '@angular/core';

import { EntitlementService } from '../tenancy/entitlement.service';
import { generateAccentTokens, generateM3Tokens } from '../../shared/utils/m3-theme.util';

/**
 * Recolors the app from the venue's brand primary color (EntitlementService.
 * brandColors) by computing token sets at runtime and writing them as
 * inline custom properties on <html> — overriding the Sass-baked defaults
 * from styles.scss without a rebuild. Two sets are written side by side:
 *  - `--nvc-a` / `--nvc-ad` / `--nvc-aw` / `--nvc-a-hover` plus the console
 *    redesign's `--ac` / `--ac-strong` / `--ac-soft` / `--ac-softer` /
 *    `--ac-line` (generateAccentTokens) — what the `nv-*` primitives read.
 *  - the ~50-token `--mat-sys-*` set (generateM3Tokens) — Angular Material
 *    itself is gone (uninstalled once `grep -rl "@angular/material" src/`
 *    went empty), but `--mat-sys-*` lives on as this app's OWN brand-
 *    reactive colour layer: `_staff-tokens.scss`'s `--nv-*` tokens
 *    (superseded by `_console-tokens.scss`'s `--nvc-*` but still read by
 *    several not-yet-migrated stylesheets — `grep -rln "mat-sys" src/app`
 *    to see which) and a handful of components read it with a raw
 *    `var(--mat-sys-*)` fallback directly. Drop this call only once that
 *    grep is empty too — dropping it while any of those still resolve
 *    through `--mat-sys-*` freezes their colour at styles.scss's static
 *    default and stops it following the venue's brand pick.
 *
 * Always computes the LIGHT M3 scheme for the mat-sys set — the portal has
 * no dark mode (removed along with ThemeService; the theme engine now
 * covers exactly one visual design, matching the guest surface's own
 * GuestThemeService, which never had one to begin with).
 *
 * Instantiated once from the shell's constructor so it's active for the
 * whole session; it re-derives tokens whenever the brand colors change; see
 * shared/utils/m3-theme.util.ts for the token math.
 */
@Injectable({ providedIn: 'root' })
export class BrandThemeService {
  private readonly entitlements = inject(EntitlementService);

  constructor() {
    effect(() => {
      const { primary } = this.entitlements.brandColors();
      const tokens = { ...generateAccentTokens(primary), ...generateM3Tokens(primary, false) };
      const root = document.documentElement.style;
      for (const [name, value] of Object.entries(tokens)) {
        root.setProperty(name, value);
      }
    });
  }
}
